import { FeatureEnum } from '@/graphql/generated';
import { NavItem } from './NavItem';
import { MaturityIcon, ResultsIcon, SurveyIcon } from '@/components/icons';
import { AppRoutes } from '@/screens/AppRoutes';
import { ReactNode } from 'react';
import { usePermissionChecker } from '@/hooks/usePermissionChecker';
import { useTranslation } from '@/hooks/useTranslation';
import { generatePath } from 'react-router';
import reportsApi from '@/slices/reports/reports.api';
import type { Report } from '@/slices/reports/reports.types';
import { useSelector } from 'react-redux';
import { selectActiveEnterpriseId } from '@/slices/activeContext/activeContext.selector';
import { isActiveEnterpriseHasLicense } from '@/slices/enterprises/enterprise.selector';

export const useDataCollectNavItems = () => {
  const { isGranted } = usePermissionChecker();

  const { t } = useTranslation();
  const items: { key: string; element: ReactNode }[] = [];

  const enterpriseId = useSelector(selectActiveEnterpriseId);
  const hasLicense = useSelector(isActiveEnterpriseHasLicense);

  const { data: enterpriseReports } =
    reportsApi.endpoints.getReportsByEnterprise.useQuery(
      { enterpriseId: enterpriseId ?? '' },
      { skip: !enterpriseId },
    );

  if (isGranted(FeatureEnum.MaturityEvaluationManagement)) {
    items.push({
      key: 'page.maturity_evaluation',
      element: (
        <NavItem
          name={t('nav:page.maturity_evaluation')}
          Icon={MaturityIcon}
          href={AppRoutes.MaturityEvaluations}
        />
      ),
    });
  }
  if (isGranted(FeatureEnum.ConsultationManagement)) {
    items.push({
      key: 'page.surveys',
      element: (
        <NavItem
          name={t('nav:page.surveys')}
          Icon={SurveyIcon}
          href={AppRoutes.Survey}
        />
      ),
    });
  }

  if (isGranted(FeatureEnum.IndicatorsManagement)) {
    // Group reports by standard and find the most recent campaign for each
    const reportsByStandard = enterpriseReports?.reduce(
      (acc, report) => {
        const clientSlug = report.slug;
        if (!acc[clientSlug]) {
          acc[clientSlug] = report;
        } else {
          // Compare campaign dates to find the most recent
          const currentCampaignDate = new Date(report.campaign.toDate);
          const existingCampaignDate = new Date(
            acc[clientSlug].campaign.toDate,
          );
          if (currentCampaignDate > existingCampaignDate) {
            acc[clientSlug] = report;
          }
        }
        return acc;
      },
      {} as Record<string, Report>,
    );

    const standardReports = reportsByStandard
      ? Object.values(reportsByStandard)
      : [];

    items.push({
      key: 'page.standards',
      element: (
        <NavItem
          name={t('nav:page.standards')}
          Icon={ResultsIcon}
          href={generatePath(AppRoutes.Reports)}
          expandable={standardReports.length > 0}
        >
          {standardReports.map((report) => (
            <NavItem
              key={report.id}
              name={t(`enum:standard.${report.clientSlug}`)}
              href={generatePath(AppRoutes.ReportHome, {
                reportId: report.id,
              })}
            />
          ))}
        </NavItem>
      ),
    });
  }

  if (isGranted(FeatureEnum.IndicatorsCollect) && hasLicense) {
    items.push({
      key: 'page.indicators_collect',
      element: (
        <NavItem
          name={t('nav:page.indicators_collect')}
          Icon={ResultsIcon}
          href={AppRoutes.IndicatorsCollect}
        />
      ),
    });
  }

  return items;
};
